/**
 * Seção "Histórico" do M6 — tabela completa de leituras do instrumento (GUPM-329).
 *
 * Genérica: desenha as colunas que `historicoDoInstrumento` declarar para o tipo. Hoje
 * só o marco superficial declara (Data | E | N | Cota | ΔE | ΔN | Recalque); os demais
 * tipos ganham a sua tabela declarando colunas no domínio, sem mexer aqui.
 */
import { useId } from 'react';
import type { HistoricoDoInstrumento } from '@/domain/instrumentos/historico';

interface Props {
  historico: HistoricoDoInstrumento;
  loading?: boolean;
}

/** pt-BR com as casas da coluna; `null` vira travessão — zero afirmaria uma medição. */
const fmt = (n: number | null | undefined, casas: number): string =>
  n == null ? '—' : n.toFixed(casas).replace('.', ',');

export default function HistoricoLeituras({ historico, loading = false }: Props) {
  const tituloId = useId();
  const { colunas, linhas, nomeDaLinha } = historico;

  return (
    <section aria-labelledby={tituloId} className="mt-4">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <h3 id={tituloId} className="text-[11px] font-bold uppercase tracking-wide text-jf-muted">
          Histórico
        </h3>
        {linhas.length > 0 && (
          <span className="text-[10px] text-jf-dim">
            {linhas.length} {linhas.length === 1 ? nomeDaLinha.singular : nomeDaLinha.plural} · mais recente primeiro
          </span>
        )}
      </div>

      {linhas.length === 0 ? (
        <p className="text-jf-muted text-sm py-4 text-center">
          {loading ? 'Carregando histórico…' : 'Sem leituras registradas.'}
        </p>
      ) : (
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-jf-surface">
              <tr className="border-b border-jf-border text-left text-jf-dim">
                <th className="pb-2 pr-3 font-semibold">Data</th>
                {colunas.map((c) => (
                  <th key={c.id} className="pb-2 pr-3 text-right font-semibold whitespace-nowrap">
                    {`${c.rotulo} (${c.unidade})`}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.ts.getTime()} className="border-b border-jf-border/40">
                  <td className="py-1.5 pr-3 whitespace-nowrap text-jf-muted">{l.rotuloData}</td>
                  {colunas.map((c) => (
                    <td
                      key={c.id}
                      className={`py-1.5 pr-3 text-right font-mono whitespace-nowrap ${
                        c.destaque ? 'font-bold' : 'text-jf-muted'
                      }`}
                    >
                      {fmt(l.valores[c.id], c.casas)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
